import { base64URLEncode } from '@/utils/base64-util';

/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0 authorization code flow
 * Implements RFC 7636 for secure OAuth flows
 */

/**
 * Generate a cryptographically secure code verifier
 * @returns {string} URL-safe base64 encoded string (43-128 characters)
 */
export function generateCodeVerifier() {
	const array = new Uint8Array(32);
	crypto.getRandomValues(array);
	return base64URLEncode(array);
}

/**
 * Generate a code challenge from a code verifier using SHA256
 * @param {string} codeVerifier - The code verifier string
 * @returns {Promise<string>} Base64URL encoded SHA256 hash
 */
export async function generateCodeChallenge(codeVerifier) {
	const encoder = new TextEncoder();
	const data = encoder.encode(codeVerifier);
	const digest = await crypto.subtle.digest('SHA-256', data);
	return base64URLEncode(new Uint8Array(digest));
}

/**
 * Generate PKCE parameters for OAuth flow
 * @returns {Promise<{codeVerifier: string, codeChallenge: string}>}
 */
export async function generatePKCEParams() {
	const codeVerifier = generateCodeVerifier();
	const codeChallenge = await generateCodeChallenge(codeVerifier);
	return {
		codeVerifier,
		codeChallenge,
	};
}
